import React, { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Table,
    Tag,
    Tooltip,
    Typography,
    Space,
    Button,
    Row,
    Col,
    Dropdown,
    Pagination,
    Badge,
    Divider,
    Modal,
    message
} from 'antd';
import {
    AmazonOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    HourglassOutlined,
    ShoppingOutlined,
    SyncOutlined,
    CarOutlined,
    EditOutlined,
    EyeOutlined,
    DownOutlined,
    CaretDownFilled,
    BgColorsOutlined,
    StarOutlined,
    SettingOutlined,
    ProductOutlined,
    ThunderboltOutlined,
    SendOutlined,
    MessageOutlined,
    UserOutlined,
    UploadOutlined,
    DownloadOutlined,
    WarningOutlined,
    ExclamationCircleOutlined,
    ExclamationCircleFilled,
    ExclamationCircleTwoTone,
    FireTwoTone,
    IdcardOutlined,
    DatabaseOutlined,
    DollarOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import '../Orders.css';
import ProductImagesGallery from '../../../components/ProductImagesGallery';
import { fetchOrderItems, sendMockup, sendFollowUpMessage, confirmShipment, bulkUpdateOrderItems, uploadOrderReport, triggerReportSync, triggerOrderProcessing, sendBulkMessage, syncFinancialData, triggerAutoConfirmTracking } from '../actions';
import OrderService from '../../../services/orderService';
import FinancialTransactionService from '../../../services/financialTransactionService';
import { ShippingInfoModal, CustomizationInfoModal, SendMockupModal, SendFollowUpMessageModal, FulfillToPrinterModal, ConfirmShipmentModal, CustomerMessagesModal, BulkEditModal, UploadOrderReportModal, ShippingTimelineModal, SendBulkMessageModal, SyncFinancialModal, ConfirmTrackingModal, RefundDetailsModal, PaymentDetailsModal, ResetOrderTempModal } from '../modals';
import { TrelloIcon, GoogleDriveIcon, LightningIcon, TruckIcon, DeliveredIcon, LocationIcon, DesignIcon } from '../../../utils/icons';
import { MemoizedOrderStatus, MemoizedCustomizationStatus } from '../../../utils/statusComponents';
import EditableCell from './EditableCell';
import EditableCellWithNoteTemplates from './EditableCellWithNoteTemplates';
import StatusOverview from './StatusOverview';
import StatusOverviewTooltip from './StatusOverviewTooltip';
import { createCellSaveHandler } from '../utils/cellSaveHandler';
import { replaceOrderItem } from '../reducers';
import { usePrinterOptions, useCategoryOptions, useUserOptions } from '../hooks/useSelectOptions';
import { financialAdjustmentPermissions, orderPermissions } from '../../../utils/permissions';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Paragraph } = Typography;

const OrdersList = ({ orderItems, isLoading, pagination, onSyncClick, onPaginationChange, clearSelectedRowsSignal, user, canSyncAmazonOrders }) => {
    const dispatch = useDispatch();
    const { printers, categories, users } = useSelector(state => state.dataCommon);
    const filters = useSelector(state => state.orders.filters);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isCustomizationModalVisible, setIsCustomizationModalVisible] = useState(false);
    const [selectedCustomization, setSelectedCustomization] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isSendMockupModalVisible, setIsSendMockupModalVisible] = useState(false);
    const [mockupReadyOrders, setMockupReadyOrders] = useState([]);
    const [isSendFollowUpMessageModalVisible, setIsSendFollowUpMessageModalVisible] = useState(false);
    const [overdueOrders, setOverdueOrders] = useState([]);
    const [isFulfillToPrinterModalVisible, setIsFulfillToPrinterModalVisible] = useState(false);
    const [selectedOrdersForFulfill, setSelectedOrdersForFulfill] = useState([]);
    const [isConfirmShipmentModalVisible, setIsConfirmShipmentModalVisible] = useState(false);
    const [shipmentReadyOrders, setShipmentReadyOrders] = useState([]);
    const [isCustomerMessagesModalVisible, setIsCustomerMessagesModalVisible] = useState(false);
    const [selectedOrderMessages, setSelectedOrderMessages] = useState([]);
    const [selectedOrderInfo, setSelectedOrderInfo] = useState(null);
    const [isBulkEditModalVisible, setIsBulkEditModalVisible] = useState(false);
    const [isUploadReportModalVisible, setIsUploadReportModalVisible] = useState(false);
    const [isShippingTimelineModalVisible, setIsShippingTimelineModalVisible] = useState(false);
    const [selectedTrackingInfo, setSelectedTrackingInfo] = useState(null);
    const [syncingFulfillmentIds, setSyncingFulfillmentIds] = useState(new Set());
    const [isSendBulkMessageModalVisible, setIsSendBulkMessageModalVisible] = useState(false);
    const [selectedOrdersForBulkMessage, setSelectedOrdersForBulkMessage] = useState([]);
    const [isSyncFinancialModalVisible, setIsSyncFinancialModalVisible] = useState(false);
    const [syncFinancialLoading, setSyncFinancialLoading] = useState(false);
    const [isConfirmTrackingModalVisible, setIsConfirmTrackingModalVisible] = useState(false);
    const [confirmTrackingLoading, setConfirmTrackingLoading] = useState(false);
    const [refreshingTrackerIds, setRefreshingTrackerIds] = useState(new Set());
    const [isRefundDetailsModalVisible, setIsRefundDetailsModalVisible] = useState(false);
    const [selectedRefundDetails, setSelectedRefundDetails] = useState(null);
    const [selectedOrderItem, setSelectedOrderItem] = useState(null);
    const [isPaymentDetailsModalVisible, setIsPaymentDetailsModalVisible] = useState(false);
    const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
    const [selectedReimbursements, setSelectedReimbursements] = useState([]);
    const [paymentDetailsLoading, setPaymentDetailsLoading] = useState(false);
    const [isResetOrderTempModalVisible, setIsResetOrderTempModalVisible] = useState(false);
    const [resetOrderTempIds, setResetOrderTempIds] = useState([]);

    // Permission checks
    const canUpdateOrders = orderPermissions.canUpdate(user);
    const canManageOrders = orderPermissions.canManage(user);
    const canBulkEdit = orderPermissions.canBulkEdit(user);
    const canSendMockup = orderPermissions.canSendMockup(user);
    const canSendFollowUpMessage = orderPermissions.canSendFollowUpMessage(user);
    const canSendBulkMessage = orderPermissions.canSendBulkMessage ? orderPermissions.canSendBulkMessage(user) : canSendFollowUpMessage; // Fallback for compatibility
    const canFulfillToPrinter = orderPermissions.canFulfillToPrinter(user);
    const canConfirmShipment = orderPermissions.canConfirmShipment(user);
    const canViewFinancialAdjustments = financialAdjustmentPermissions.canRead(user) ||
        financialAdjustmentPermissions.canManage(user);

    const openPaymentDetails = useCallback(async (record) => {
        setSelectedPaymentDetails(record.itemPaymentDetails || {
            paymentDate: null,
            charges: {},
            fees: {},
            taxWithheld: {},
            promotions: { adjustments: [], total: 0 },
            netEarnings: 0
        });
        setSelectedOrderItem(record);
        setSelectedReimbursements([]);
        setIsPaymentDetailsModalVisible(true);
        if (!canViewFinancialAdjustments) {
            setPaymentDetailsLoading(false);
            return;
        }
        setPaymentDetailsLoading(true);
        try {
            const response = await FinancialTransactionService.getOrderItemReimbursements(record.id);
            setSelectedReimbursements(response.data?.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || 'Không thể tải reimbursement details');
        } finally {
            setPaymentDetailsLoading(false);
        }
    }, [canViewFinancialAdjustments]);
    const canUploadReport = orderPermissions.canUploadReport(user);
    const canExportCSV = orderPermissions.canExportCSV(user);
    const canViewCustomerMessages = orderPermissions.canViewCustomerMessages(user);
    const canViewFinancialData = orderPermissions.canViewFinancialData(user);
    const canViewSensitiveInfo = orderPermissions.canViewSensitiveInfo(user);
    const canViewShippingInfo = orderPermissions.canViewShippingInfo(user);
    const canViewBaseCost = orderPermissions.canViewBaseCost(user);

    // Use the extracted printer utilities
    const printerOptions = usePrinterOptions(printers);
    const categoryOptions = useCategoryOptions(categories);
    const userOptions = useUserOptions(users);

    // Clear selected rows when filter is applied or cleared
    useEffect(() => {
        if (clearSelectedRowsSignal) {
            setSelectedRowKeys([]);
        }
    }, [clearSelectedRowsSignal]);

    // Create the cell save handler with permission check
    const handleCellSave = useCallback(
        (id, column, value) => {
            if (!canUpdateOrders) {
                message.warning('Bạn không có quyền chỉnh sửa đơn hàng');
                return;
            }
            // Use optimized handler that doesn't reload entire table
            const originalHandler = createCellSaveHandler(dispatch, pagination, fetchOrderItems, false);
            return originalHandler(id, column, value);
        },
        [dispatch, pagination, fetchOrderItems, canUpdateOrders]
    );

    // Memoized processed data to avoid recalculating on every render
    const processedData = useMemo(() => {
        if (!orderItems || orderItems.length === 0) return [];
        console.log('Processing order items for display...');

        // Ensure consistent sorting by sellerId and amazonOrderId before grouping
        const sortedOrderItems = [...orderItems].sort((a, b) => {
            // First sort by sellerId (ascending)
            const sellerIdA = a.sellerId || '';
            const sellerIdB = b.sellerId || '';
            if (sellerIdA !== sellerIdB) {
                return sellerIdA.localeCompare(sellerIdB);
            }

            // Second sort by purchaseDate (YYYY-MM-DD format, descending)
            const purchaseDateA = a.order?.purchaseDate ? dayjs(a.order.purchaseDate).format('YYYY-MM-DD') : '';
            const purchaseDateB = b.order?.purchaseDate ? dayjs(b.order.purchaseDate).format('YYYY-MM-DD') : '';
            if (purchaseDateA !== purchaseDateB) {
                return purchaseDateB.localeCompare(purchaseDateA);
            }

            // Then sort by amazonOrderId (ascending)
            const amazonOrderIdA = a.amazonOrderId || '';
            const amazonOrderIdB = b.amazonOrderId || '';
            return amazonOrderIdA.localeCompare(amazonOrderIdB);
        });

        // Group by orderId
        const groupedData = sortedOrderItems.reduce((acc, item) => {
            const orderId = item.order?.id;
            if (!acc[orderId]) {
                acc[orderId] = [];
            }
            acc[orderId].push(item);
            return acc;
        }, {});

        // Create data with spans
        const result = [];
        Object.values(groupedData).forEach(orderItems => {
            // Calculate total earnings for this order
            // Amounts from Amazon are signed, so refunds and reimbursements are added directly.
            const totalEarnings = orderItems.reduce((sum, item) => {
                const earnings = parseFloat(item.itemEarnings || 0);
                const refund = parseFloat(item.itemRefundAmount || 0);
                const reimbursement = parseFloat(item.reimbursementAmount || 0);
                return sum + earnings + refund + reimbursement;
            }, 0);

            orderItems.forEach((item, index) => {
                result.push({
                    ...item,
                    _isFirstItemInOrder: index === 0,
                    _orderItemCount: orderItems.length,
                    _orderTotalEarnings: totalEarnings.toFixed(2)
                });
            });
        });

        return result;
    }, [orderItems]);

    // Xử lý sự kiện thay đổi trang
    const handleTableChange = (paginationParams, filters, sorter) => {
        // Use the new pagination handler passed from parent
        if (onPaginationChange) {
            onPaginationChange(paginationParams.current, paginationParams.pageSize);
        } else {
            // Fallback to old behavior if onPaginationChange is not provided
            dispatch(fetchOrderItems(
                paginationParams.current,
                paginationParams.pageSize
            ));
        }
    };

    // Memoized row selection handlers with immediate state update
    const handleRowSelectionChange = useCallback((newSelectedRowKeys) => {
        // Use functional update to ensure immediate state change
        setSelectedRowKeys(prevKeys => {
            // Force immediate update without batching
            return [...newSelectedRowKeys];
        });
    }, []);

    const handleRowSelect = useCallback((record, selected, selectedRows, nativeEvent) => {
        // Prevent event bubbling that might cause delays
        if (nativeEvent) {
            nativeEvent.stopPropagation();
        }
    }, []);

    const handleSelectAll = useCallback((selected, selectedRows, changeRows) => {
        // Optional: Add immediate feedback for select all
        console.log('Select all triggered:', selected);
    }, []);

    // Handle customer messages modal
    const handleMessagesClick = useCallback((orderMessages, orderRecord) => {
        if (!canViewCustomerMessages) {
            message.warning('Bạn không có quyền xem tin nhắn khách hàng');
            return;
        }
        setSelectedOrderMessages(orderMessages);
        setSelectedOrderInfo(orderRecord);
        setIsCustomerMessagesModalVisible(true);
    }, [canViewCustomerMessages]);

    // Handle order update callback for ShippingInfoModal
    const handleOrderUpdated = useCallback((updatedOrderData) => {
        // Refresh the order items to get latest data
        dispatch(fetchOrderItems(pagination.current, pagination.pageSize));

        // Update selected order with new data if it's the same order
        if (selectedOrder && (selectedOrder.id === updatedOrderData.id || selectedOrder.order?.id === updatedOrderData.id)) {
            setSelectedOrder(prev => ({
                ...prev,
                ...updatedOrderData
            }));
        }
    }, [dispatch, pagination, selectedOrder]);

    // Handle sync fulfillment info for single order
    const handleSyncFulfillmentInfo = useCallback(async (fulfillmentId, orderItemId) => {
        if (!fulfillmentId || syncingFulfillmentIds.has(fulfillmentId)) return;

        setSyncingFulfillmentIds(prev => new Set([...prev, fulfillmentId]));

        try {
            const result = await OrderService.syncSingleFulfillmentInfo(fulfillmentId);

            if (result.success) {
                message.success(`Successfully synced fulfillment info for ${fulfillmentId}`);
                // Fetch updated item and patch Redux state - không reload cả trang
                if (orderItemId) {
                    try {
                        const updatedItem = await OrderService.getOrderItemById(orderItemId);
                        if (updatedItem) {
                            dispatch(replaceOrderItem(updatedItem));
                        }
                    } catch (fetchError) {
                        console.warn('Could not fetch updated order item, falling back to full reload:', fetchError);
                        if (onPaginationChange) {
                            onPaginationChange(pagination.current, pagination.pageSize);
                        } else {
                            dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
                        }
                    }
                }
            } else {
                message.error(result.message || `Failed to sync fulfillment info for ${fulfillmentId}`);
            }
        } catch (error) {
            console.error('Error syncing fulfillment info:', error);
            message.error('Error syncing fulfillment info');
        } finally {
            setSyncingFulfillmentIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(fulfillmentId);
                return newSet;
            });
        }
    }, [syncingFulfillmentIds, pagination, onPaginationChange, dispatch]);

    // Handle refresh tracking info for single tracker
    const handleRefreshTrackingInfo = useCallback(async (trackerId) => {
        if (!trackerId || refreshingTrackerIds.has(trackerId)) return;

        setRefreshingTrackerIds(prev => new Set([...prev, trackerId]));

        try {
            const result = await OrderService.refreshTrackingInfo([trackerId]);

            if (result.success) {
                const { processedCount, skippedCount, errorCount } = result.data;
                
                if (processedCount > 0) {
                    message.success(`Refreshed tracking info successfully (${processedCount} updated)`);
                } else if (skippedCount > 0) {
                    message.info(`Tracking info already up to date`);
                } else {
                    message.warning(`Failed to refresh tracking info`);
                }
                
                // Reload current page data
                if (onPaginationChange) {
                    onPaginationChange(pagination.current, pagination.pageSize);
                } else {
                    dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
                }
            } else {
                message.error(result.message || 'Failed to refresh tracking info');
            }
        } catch (error) {
            console.error('Error refreshing tracking info:', error);
            message.error('Error refreshing tracking info');
        } finally {
            setRefreshingTrackerIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(trackerId);
                return newSet;
            });
        }
    }, [refreshingTrackerIds, pagination, onPaginationChange, dispatch]);

    // Optimized row selection configuration with performance settings
    const rowSelection = useMemo(() => ({
        selectedRowKeys,
        onChange: handleRowSelectionChange,
        onSelectAll: handleSelectAll,
        onSelect: handleRowSelect,
        preserveSelectedRowKeys: true,
        // Add these performance optimizations
        checkStrictly: false, // Faster selection
        renderCell: undefined, // Use default rendering for speed
        columnWidth: 32, // Fixed width for consistent layout
        fixed: true, // Fix selection column
    }), [selectedRowKeys, handleRowSelectionChange, handleSelectAll, handleRowSelect]);

    // Helper function to check if a column contains financial data
    const isFinancialColumn = (column) => {
        const financialKeys = ['itemEarnings', 'totalEarnings', 'itemPrice', 'shippingPrice', 'itemRefundAmount', 'reimbursementAmount'];
        return financialKeys.includes(column.key) ||
            (column.dataIndex && financialKeys.some(key =>
                Array.isArray(column.dataIndex) ? column.dataIndex.includes(key) : column.dataIndex === key
            ));
    };

    // Helper function to check if a column contains base cost data
    const isBaseCostColumn = (column) => column.key === 'baseCost';

    // Memoized columns definition to prevent recreation with permission checks
    const columns = useMemo(() => {
        const baseColumns = [
            {
                title: <Tooltip title="STT">#</Tooltip>,
                dataIndex: 'id',
                key: 'index',
                width: 65,
                align: 'center',
                render: (_value, _record, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
                fixed: 'left'
            },
            {
                title: <Tooltip title="Mã đơn hàng Amazon">Order ID</Tooltip>,
                dataIndex: ['order', 'amazonOrderId'],
                key: 'amazonOrderId',
                width: 235,
                fixed: 'left',
                render: (text, record) => (
                    <Space className="no-line-through copyable-text" style={{ height: 32 }}>
                        {record.order?.platform === 'amazon' && (
                            <AmazonOutlined style={{ color: '#FF9900', fontSize: 15, marginRight: 4, verticalAlign: 'middle' }} />
                        )}
                        <Text
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                window.open(`/orders/${record.order?.id}`, '_blank');
                            }}
                        >
                            {text ? text : '-'}
                        </Text>
                        <Text copyable={{ text: text }} className='copyable-icon' />
                    </Space>
                ),
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                })
            },
            {
                title: <Tooltip title="Kênh Fulfillment"><ShoppingOutlined /></Tooltip>,
                dataIndex: ['order', 'fulfillmentChannel'],
                key: 'fulfillmentChannel',
                width: 70,
                align: 'center',
                fixed: 'left',
                render: (text, record) => {
                    const hasCustomization = record.customizationInfo && record.customizationInfo.isNotCustomization !== true;
                    return (
                        <span className="no-line-through" >
                            {hasCustomization ?
                                <Tooltip title="This order has customization">
                                    <span style={{ position: 'absolute', left: -3, marginTop: 3, color: '#1890ff' }}><BgColorsOutlined /></span>
                                </Tooltip>
                                : null}
                            {text === 'AFN' ?
                                <Tag color="orange" style={{ textAlign: 'center', fontSize: 12, width: 43, marginRight: 0 }}>FBA</Tag> :
                                text === 'MFN' ?
                                    <Tag color="blue" style={{ textAlign: 'center', fontSize: 12, width: 43, marginRight: 0 }}>FBM</Tag> :
                                    <Tag color="default" style={{ fontSize: 12 }}>{text || '-'}</Tag>
                            }
                        </span >
                    );

                },
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                })
            },
            {
                title: <Tooltip title="Nhân viên hỗ trợ">Supporter</Tooltip>,
                key: 'supporter',
                width: 140,
                align: 'center',
                fixed: 'left',
                render: (text, record) => {
                    const supporterId = record.supporterId;

                    return (
                        <EditableCell
                            value={supporterId}
                            record={record}
                            column="supporterId"
                            onSave={handleCellSave}
                            options={userOptions}
                            placeholder="Select supporter..."
                            loading={isLoading}
                            inputType="select"
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Danh mục sản phẩm">Category</Tooltip>,
                key: 'category',
                width: 150,
                align: 'center',
                fixed: 'left',
                render: (text, record) => {
                    const categoryId = record.categoryId;

                    return (
                        <EditableCell
                            value={categoryId}
                            record={record}
                            column="category"
                            onSave={handleCellSave}
                            options={categoryOptions}
                            placeholder="Select category..."
                            loading={isLoading}
                            inputType="select"
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Tài khoản bán hàng">Seller Account</Tooltip>,
                dataIndex: ['sellerAccount', 'name'],
                key: 'sellerAccountName',
                width: 167,
                align: 'center',
                render: (text, record) => (
                    <span className="no-line-through">
                        {canViewSensitiveInfo ? (text || record.order?.sellerAccount?.name || '-') : <Text style={{ fontSize: 11, color: '#bbb', letterSpacing: 2 }}>***</Text>}
                    </span>
                )
            },
            {
                title: <Tooltip title="Ngày đặt hàng (giờ Mỹ)"><span>🇺🇸 Purchase Date</span></Tooltip>,
                dataIndex: ['order', 'purchaseDate'],
                key: 'purchaseDate',
                width: 170,
                align: 'center',
                render: (text) => {
                    if (!text) return <span className="no-line-through">-</span>;

                    // Convert to US Pacific Time and format
                    const usTime = dayjs(text).tz('America/Los_Angeles');
                    return (
                        <span className="no-line-through">
                            <div style={{ lineHeight: 1.2 }}>
                                <div style={{ fontSize: 10, color: '#666' }}>
                                    {usTime.format('hh:mm A')} PT
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 500 }}>
                                    {usTime.format('DD/MM/YYYY')}
                                </div>
                            </div>
                        </span>
                    );
                },
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                }),
            },
            {
                title: <Tooltip title="Mã SKU">SKU / ASIN</Tooltip>,
                dataIndex: 'sellerSKU',
                key: 'sellerSKU',
                width: 360,
                render: (text, record) =>
                    <Row style={{ flexFlow: 'nowrap' }} align='middle' gutter={8}>
                        <Col>
                            <ProductImagesGallery skuItem={record.skuItem} maxVisibleImages={1} showRemainingCount={false} imageSize={35} />
                        </Col>
                        <Col>
                            {canViewSensitiveInfo
                                ? <a target='_blank' style={{ fontSize: 11 }} href={`https://www.amazon.com/dp/${record.asin}`}>{record.asin}</a>
                                : <Text style={{ fontSize: 11, color: '#bbb', letterSpacing: 2 }}>***</Text>
                            }<br></br>
                            <Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: 1 }} className="no-line-through">{text || '-'}</Paragraph>
                        </Col>
                    </Row>

            },
            {
                title: <Tooltip title="Sản phẩm">Product / Order Item Id</Tooltip>,
                dataIndex: 'title',
                key: 'title',
                width: 400,
                ellipsis: true,
                render: (text, record) => (
                    <div>
                        <span target='_blank' style={{ fontSize: 11, color: '#999' }} href={`https://www.amazon.com/dp/${record.asin}`}>{record.orderItemId}</span><br></br>
                        <Tooltip title={text}>
                            <Paragraph className="no-line-through" style={{ marginBottom: 0 }} ellipsis={{ rows: 1 }} >{text ? text : '-'}</Paragraph>
                        </Tooltip>
                    </div>
                ),
            },
            {
                title: <Tooltip title="Tùy chỉnh">Customization</Tooltip>,
                key: 'customization',
                width: 120,
                align: 'center',
                render: (text, record) => {
                    const hasCustomization = record.customizationInfo;
                    const fulfillmentChannel = record.order?.fulfillmentChannel;
                    const quantityOrdered = Number(record.quantityOrdered || 0);
                    const isCanceledItem = record.fulfillmentStatus === 'Canceled' && quantityOrdered <= 0;

                    if (isCanceledItem) {
                        return <Tag color="default">Canceled item</Tag>;
                    }

                    if (quantityOrdered <= 0) {
                        return <Tag color="default">Qty 0</Tag>;
                    }

                    // Case 1: MFN with no customization - show "Waiting for sync"
                    if (fulfillmentChannel === 'MFN' && !hasCustomization) {
                        return (
                            <div style={{ color: '#999', fontSize: 11 }}>
                                <SyncOutlined /> Waiting for sync
                            </div>
                        );
                    }

                    // Case 2: Has customization but isCustomization is false
                    if (hasCustomization && hasCustomization.isNotCustomization === true) {
                        return (
                            <span style={{ color: '#999', fontSize: 11 }}>
                                Non-custom order
                            </span>
                        );
                    }

                    // Case 3: Has customization and it's a real customization order
                    if (hasCustomization && hasCustomization.isNotCustomization !== true) {
                        return (
                            <Button
                                variant="dashed"
                                color="primary"
                                size="small"
                                icon={<BgColorsOutlined />}
                                onClick={() => {
                                    setSelectedCustomization(record.customizationInfo);
                                    setIsCustomizationModalVisible(true);
                                }}
                            >
                                Details
                            </Button>
                        );
                    }

                    // Default case: no customization
                    return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
                }
            },
            {
                title: <Tooltip title="Ghi chú">Note</Tooltip>,
                key: 'note',
                width: 250,
                align: 'left',
                ellipsis: true,
                render: (text, record) => {
                    const note = record.note;
                    return (
                        <EditableCellWithNoteTemplates
                            value={note}
                            record={record}
                            column="note"
                            onSave={handleCellSave}
                            placeholder="Add note..."
                            loading={isLoading}
                            inputType="note-autocomplete"
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Nhà in">Printer</Tooltip>,
                key: 'printer',
                width: 170,
                align: 'center',
                render: (text, record) => {
                    const printerId = record.printerId;

                    return (
                        <EditableCell
                            value={printerId}
                            record={record}
                            column="printer"
                            onSave={handleCellSave}
                            options={printerOptions}
                            placeholder="Select printer..."
                            loading={isLoading}
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Giá gốc">Base Cost</Tooltip>,
                key: 'baseCost',
                width: 120,
                align: 'right',
                render: (text, record) => {
                    return (
                        <EditableCell
                            value={record.baseCost}
                            record={record}
                            column="baseCost"
                            onSave={handleCellSave}
                            placeholder="Enter cost..."
                            loading={isLoading}
                            inputType="number"
                            prefix="$"
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Số lượng">Qty</Tooltip>,
                dataIndex: 'quantityOrdered',
                key: 'quantityOrdered',
                width: 70,
                align: 'center',
                render: (quantityOrdered, record) => <span className="no-line-through">{quantityOrdered || 0}</span>
            },
            {
                title: <Tooltip title="Giá + phí vận chuyển">Price + Shipping</Tooltip>,
                key: 'priceWithShipping',
                width: 120,
                align: 'right',
                render: (_, record) => {
                    const itemPrice = parseFloat(record.itemPriceAmount || 0);
                    const shippingPrice = parseFloat(record.shippingPriceAmount || 0);
                    const shippingDiscount = parseFloat(record.shippingDiscountAmount || 0);
                    const total = itemPrice + shippingPrice - shippingDiscount;

                    return <span className="no-line-through">{`$${total.toFixed(2)}`}</span>;
                }
            },
            {
                title: <Tooltip title="Giá sau thuế">Earnings</Tooltip>,
                dataIndex: 'itemEarnings',
                key: 'itemEarnings',
                width: 120,
                align: 'right',
                render: (text, record) => {
                    if (!text) {
                        return <span className="no-line-through" style={{ color: '#999' }}>-</span>;
                    }
                    
                    const hasPaymentDetails = record.itemPaymentDetails && typeof record.itemPaymentDetails === 'object';
                    const earnings = parseFloat(text);
                    const isEstimated = record.isEarningsEstimated === true;
                    const displayValue = isEstimated
                        ? `~$${Math.abs(earnings).toFixed(2)}`
                        : `$${Math.abs(earnings).toFixed(2)}`;
                    
                    const earningsSpan = (
                        <span 
                            className="no-line-through"
                            style={{
                                color: isEstimated ? '#faad14' : (earnings < 0 ? '#d32f2f' : '#4caf50'),
                                fontWeight: 500,
                                cursor: hasPaymentDetails ? 'pointer' : 'default',
                                textDecoration: hasPaymentDetails ? 'underline' : 'none'
                            }}
                            onClick={() => {
                                if (hasPaymentDetails) {
                                    openPaymentDetails(record);
                                }
                            }}
                        >
                            {displayValue}{isEstimated && <span style={{ marginLeft: 2, fontSize: 11 }}>⚡</span>}
                        </span>
                    );

                    if (isEstimated) {
                        return (
                            <Tooltip title="Giá tạm tính từ listing price (FBA Pending)">
                                {earningsSpan}
                            </Tooltip>
                        );
                    }

                    return earningsSpan;
                }
            },
            {
                title: <Tooltip title="Tổng số tiền refund">Refund</Tooltip>,
                dataIndex: 'itemRefundAmount',
                key: 'itemRefundAmount',
                width: 100,
                align: 'right',
                render: (text, record) => {
                    if (!text || text === 0) {
                        return <span className="no-line-through" style={{ color: '#999' }}>-</span>;
                    }
                    
                    const hasDetails = record.itemRefundDetails && Array.isArray(record.itemRefundDetails) && record.itemRefundDetails.length > 0;
                    const refundAmount = parseFloat(text);
                    const displayValue = `$${Math.abs(refundAmount).toFixed(2)}`;
                    
                    return (
                        <span 
                            className="no-line-through"
                            style={{
                                color: refundAmount < 0 ? '#d32f2f' : '#4caf50',
                                fontWeight: 500,
                                cursor: hasDetails ? 'pointer' : 'default',
                                textDecoration: hasDetails ? 'underline' : 'none'
                            }}
                            onClick={() => {
                                if (hasDetails) {
                                    setSelectedRefundDetails(record.itemRefundDetails);
                                    setSelectedOrderItem(record);
                                    setIsRefundDetailsModalVisible(true);
                                }
                            }}
                        >
                            {refundAmount < 0 ? '-' : '+'}{displayValue}
                        </span>
                    );
                }
            },
            {
                title: <Tooltip title="FBA Inventory Reimbursement đã liên kết">Reimbursement</Tooltip>,
                dataIndex: 'reimbursementAmount',
                key: 'reimbursementAmount',
                width: 125,
                align: 'right',
                render: (text, record) => {
                    const amount = parseFloat(text || 0);
                    if (amount === 0) {
                        return <span className="no-line-through" style={{ color: '#999' }}>-</span>;
                    }
                    return (
                        <span
                            className="no-line-through"
                            style={{
                                color: amount < 0 ? '#d32f2f' : '#4caf50',
                                fontWeight: 500,
                                cursor: 'pointer',
                                textDecoration: 'underline'
                            }}
                            onClick={() => openPaymentDetails(record)}
                        >
                            {amount < 0 ? '-' : '+'}${Math.abs(amount).toFixed(2)}
                        </span>
                    );
                }
            },
            {
                title: <Tooltip title="Tổng thu nhập của đơn hàng">Total Earnings</Tooltip>,
                key: 'totalEarnings',
                width: 120,
                align: 'right',
                render: (text, record) => {
                    const totalEarnings = parseFloat(record._orderTotalEarnings);
                    return (
                        <span 
                            className="no-line-through"
                            style={{
                                color: totalEarnings < 0 ? '#d32f2f' : '#4caf50',
                                fontWeight: 500
                            }}
                        >
                            {`$${record._orderTotalEarnings}`}
                        </span>
                    );
                },
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                }),
            },
            {
                title: <Tooltip title="Ngày giao hàng trễ nhất (giờ Mỹ)"><span>🇺🇸 Latest<br></br> Ship Date</span></Tooltip>,
                dataIndex: ['order', 'latestShipDate'],
                key: 'latestShipDate',
                width: 140,
                align: 'center',
                render: (text, record) => {
                    if (!text) {
                        return <span className="no-line-through">-</span>;
                    }

                    // Convert to US Pacific Time
                    const shipDate = dayjs(text);
                    const usTime = dayjs(text).tz('America/Los_Angeles');
                    const today = dayjs();
                    const daysDiff = shipDate.diff(today, 'day');

                    // Check if ship date is exactly 2 days ago (T-2)
                    const isT2 = daysDiff <= 2;

                    // Don't show warning if shipment is already confirmed
                    const isShipmentConfirmed = record.isConfirmShipped === true;

                    // Only show warning for MFN orders
                    const isMFN = record.order?.fulfillmentChannel === 'MFN';

                    // Don't show warning if order is already Shipped or Canceled
                    const orderStatus = record.order?.orderStatus;
                    const isShippedOrCanceled = orderStatus === 'Shipped' || orderStatus === 'Canceled';

                    const showWarning = isT2 && !isShipmentConfirmed && isMFN && !isShippedOrCanceled;

                    return (
                        <span className="no-line-through" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <span style={{ color: showWarning ? 'red' : 'inherit' }}>{usTime.format('DD/MM/YYYY')}</span>
                            {showWarning && (
                                <Tooltip title={`Cần xác nhận shipment`}>
                                    <ExclamationCircleTwoTone
                                        twoToneColor='red'
                                    />
                                </Tooltip>
                            )}
                        </span>
                    );
                },
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                }),
            },
            {
                title: <Tooltip title="Ngày giao hàng trễ nhất (giờ Mỹ)"><span>🇺🇸 Latest<br></br>Delivery Date</span></Tooltip>,
                dataIndex: ['order', 'latestDeliveryDate'],
                key: 'deliveryDate',
                width: 140,
                align: 'center',
                render: (text, record) => {
                    if (!text) {
                        return <span className="no-line-through">-</span>;
                    }

                    // Convert to US Pacific Time
                    const deliveryDate = dayjs(text);
                    const usTime = dayjs(text).tz('America/Los_Angeles');
                    const todayUS = dayjs().tz('America/Los_Angeles');

                    // Compare dates in YYYY-MM-DD format
                    const deliveryDateStr = usTime.format('YYYY-MM-DD');
                    const todayStr = todayUS.format('YYYY-MM-DD');
                    const daysDiff = dayjs(deliveryDateStr).diff(dayjs(todayStr), 'day');

                    // Check if delivery date is within 2 days
                    const isWithin2Days = daysDiff <= 2;

                    // Don't show warning if shipment is already confirmed
                    const isShipmentConfirmed = record.isConfirmShipped === true;

                    // Only show warning for MFN orders
                    const isMFN = record.order?.fulfillmentChannel === 'MFN';

                    // Don't show warning if order is already Shipped or Canceled
                    const orderStatus = record.order?.orderStatus;
                    const isShippedOrCanceled = orderStatus === 'Shipped' || orderStatus === 'Canceled';

                    const showWarning = isWithin2Days && isMFN && orderStatus !== 'Canceled';

                    return (
                        <span className="no-line-through" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <span style={{ color: showWarning ? 'red' : 'inherit' }}>{usTime.format('DD/MM/YYYY')}</span>
                            {showWarning && (
                                <Tooltip title={`Sắp tới ngày giao hàng`}>
                                    <ExclamationCircleTwoTone
                                        twoToneColor='red'
                                    />
                                </Tooltip>
                            )}
                        </span>
                    );
                },
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                }),
            },
            {
                title: <Tooltip title="Trạng thái tùy chỉnh">Customization<br /> Status</Tooltip>,
                dataIndex: 'customizationStatus',
                key: 'customizationStatus',
                width: 230,
                align: 'center',
                render: (status, record) => {
                    const customizationStatusOptions = [
                        { value: 'NoCustomization', label: 'No Customization' },
                        { value: 'TaskCreated', label: 'Task Created' },
                        { value: 'Doing', label: 'Doing' },
                        { value: 'InternalReview', label: 'Internal Review' },
                        { value: 'Rework', label: 'Rework' },
                        { value: 'MockupReady', label: 'Mockup Ready' },
                        { value: 'MockupSent', label: 'Mockup Sent' },
                        { value: 'Confirming', label: 'Confirming' },
                        { value: 'CustomerConfirmed', label: 'Customer Confirmed' },
                        { value: 'ConfirmedNoReply', label: 'Confirmed No Reply' },
                        { value: 'AutoFulfill', label: 'Auto Fulfill' }
                    ];

                    return (
                        <EditableCell
                            value={status}
                            record={record}
                            column="customizationStatus"
                            onSave={handleCellSave}
                            options={customizationStatusOptions}
                            placeholder="Select status..."
                            loading={isLoading}
                            inputType="select"
                            displayComponent={(currentValue) => (
                                <span className="no-line-through">
                                    <MemoizedCustomizationStatus
                                        status={currentValue}
                                        trelloUrl={record.trelloUrl}
                                        customizationStorageUrl={record.customizationStorageUrl}
                                        mockupSentAt={record.mockupSentAt}
                                    />
                                </span>
                            )}
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Trạng thái thực hiện">Fulfillment Status</Tooltip>,
                dataIndex: 'fulfillmentStatus',
                key: 'fulfillmentStatus',
                width: 150,
                align: 'center',
                render: (status, record) => {
                    const effectiveStatus = (record.fulfillmentStatus === 'Canceled' && Number(record.quantityOrdered || 0) <= 0)
                        ? 'Canceled'
                        : status;
                    const fulfillmentStatusOptions = [
                        { value: 'Unfulfilled', label: 'Unfulfilled' },
                        { value: 'Submitted', label: 'Submitted' },
                        { value: 'Fulfilled', label: 'Fulfilled' },
                        { value: 'Shipped', label: 'Shipped' },
                        { value: 'Canceled', label: 'Canceled' },
                        { value: 'Failed', label: 'Failed' }
                    ];

                    // Status color mapping
                    const getStatusColor = (status) => {
                        switch (status) {
                            case 'Unfulfilled':
                                return 'default';
                            case 'Fulfilled':
                                return 'blue';
                            case 'Shipped':
                                return 'green';
                            case 'Canceled':
                                return 'red';
                            case 'Submitted':
                                return 'orange';
                            case 'Failed':
                                return 'red';
                            default:
                                return 'default';
                        }
                    };

                    return (
                        <EditableCell
                            value={effectiveStatus}
                            record={record}
                            column="fulfillmentStatus"
                            onSave={handleCellSave}
                            options={fulfillmentStatusOptions}
                            placeholder="Select status..."
                            loading={isLoading}
                            inputType="select"
                            displayComponent={(currentValue) => (
                                <span className="no-line-through"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        width: '170px'
                                    }}
                                >
                                    {currentValue ? (
                                        <>
                                            <Tag color={getStatusColor(currentValue)} style={{ marginRight: 0, width: 100, textAlign: 'center' }}>
                                                {currentValue}
                                            </Tag>
                                            {currentValue === 'Failed' && record.fulfillmentErrorReason && (
                                                <Tooltip title={record.fulfillmentErrorReason}>
                                                    <ExclamationCircleOutlined style={{ color: 'red', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                        </>
                                    ) : (
                                        <span style={{ color: '#999', fontSize: 11 }}>-</span>
                                    )}
                                </span>
                            )}
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Mã định danh fulfillment">Fulfillment ID</Tooltip>,
                dataIndex: 'fulfillmentId',
                key: 'fulfillmentId',
                width: 190,
                align: 'center',
                render: (fulfillmentId, record) => {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <EditableCell
                                value={fulfillmentId}
                                record={record}
                                column="fulfillmentId"
                                onSave={handleCellSave}
                                placeholder="Enter fulfillment ID..."
                                loading={isLoading}
                                inputType="text"
                                displayComponent={(currentValue) => (
                                    <span className="no-line-through">
                                        {currentValue ? (
                                            <Text
                                                style={{
                                                    color: '#1890ff',
                                                    cursor: 'pointer'
                                                }}
                                                copyable={{ text: currentValue }}
                                            >
                                                {currentValue}
                                            </Text>
                                        ) : (
                                            <span style={{ color: '#999', fontSize: 11 }}>-</span>
                                        )}
                                    </span>
                                )}
                            />
                            {fulfillmentId && canUpdateOrders && (
                                <Tooltip title={`Sync fulfillment info for ${fulfillmentId}`}>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<SyncOutlined spin={syncingFulfillmentIds.has(fulfillmentId)} />}
                                        loading={syncingFulfillmentIds.has(fulfillmentId)}
                                        disabled={syncingFulfillmentIds.has(fulfillmentId)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSyncFulfillmentInfo(fulfillmentId, record.id);
                                        }}
                                        style={{
                                            color: '#52c41a',
                                            padding: '0 4px',
                                            height: 20,
                                            minWidth: 20
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    );
                }
            },
            {
                title: <Tooltip title="Xác nhận đã giao hàng">Confirm Shipment</Tooltip>,
                dataIndex: 'isConfirmShipped',
                key: 'isConfirmShipped',
                width: 135,
                align: 'center',
                render: (isConfirmed, record) => {
                    const confirmShipmentOptions = [
                        { value: true, label: 'Confirmed' },
                        { value: false, label: 'Pending' }
                    ];

                    return (
                        <EditableCell
                            value={isConfirmed}
                            record={record}
                            column="isConfirmShipped"
                            onSave={handleCellSave}
                            options={confirmShipmentOptions}
                            placeholder="Select status..."
                            loading={isLoading}
                            inputType="select"
                            displayComponent={(currentValue) => (
                                <span className="no-line-through">
                                    {currentValue ? (
                                        <Tag color="green" style={{ marginRight: 0, fontSize: 11, width: 85, textAlign: 'center' }}>
                                            <CheckCircleOutlined style={{ marginRight: 4 }} />
                                            Confirmed
                                        </Tag>
                                    ) : (
                                        <Tag color="default" style={{ marginRight: 0, fontSize: 11, width: 85, textAlign: 'center' }}>
                                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                                            Pending
                                        </Tag>
                                    )}
                                </span>
                            )}
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Shipping Information">Shipping info</Tooltip>,
                key: 'shippingInfo',
                width: 120,
                align: 'center',
                render: (text, record) => {
                    // Check if fulfillment channel is AFN
                    const isAFN = record.order?.fulfillmentChannel === 'AFN';
                    const hasShippingInfo = record.order?.shipName;

                    // For AFN orders, shipping is handled by Amazon
                    if (isAFN) {
                        return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
                    }

                    // For MFN orders, show clickable text/button
                    if (hasShippingInfo) {
                        return (
                            <Button
                                size="small"
                                variant="dashed"
                                color="primary"
                                icon={<CarOutlined />}
                                onClick={() => {
                                    setSelectedOrder(record.order);
                                    setIsModalVisible(true);
                                }}
                            >
                                View
                            </Button>
                        );
                    } else {
                        // Show clickable "Waiting for sync" text with hover effect like EditableCell
                        return (
                            <div
                                className="editable-cell"
                                onClick={() => {
                                    setSelectedOrder(record.order);
                                    setIsModalVisible(true);
                                }}
                            >
                                <span style={{ color: '#999', fontSize: 11 }}>
                                    <SyncOutlined /> Waiting for sync
                                </span>
                            </div>
                        );
                    }
                },
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                })
            },
            {
                title: <Tooltip title="Phương thức giao hàng">Delivery Method</Tooltip>,
                key: 'deliveryMethod',
                width: 120,
                align: 'center',
                render: (text, record) => {
                    const shipServiceLevel = record.order?.shipServiceLevel;

                    if (!shipServiceLevel) {
                        return <span style={{ color: '#999' }}>-</span>;
                    }

                    return shipServiceLevel === 'Expedited' ? (
                        <Tag color="blue" style={{ marginRight: 0 }}>
                            <LightningIcon /> Expedited
                        </Tag>
                    ) : (
                        <span>Standard</span>
                    );
                },
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                })
            },
            {
                title: <Tooltip title="Hãng vận chuyển">Carrier</Tooltip>,
                key: 'carrier',
                width: 130,
                align: 'left',
                render: (text, record) => {
                    const carrierOptions = [
                        { value: 'USPS', label: 'USPS' },
                        { value: 'UPS', label: 'UPS' },
                        { value: 'Yanwen', label: 'Yanwen' },
                        { value: 'Yunexpress', label: 'Yunexpress' },
                        { value: 'FedEx', label: 'FedEx' },
                        { value: 'DHL', label: 'DHL' },
                        { value: 'AmazonLogistics', label: 'Amazon Logistics' },
                        { value: 'OnTrac', label: 'OnTrac' },
                        { value: 'Lasership', label: 'Lasership' },
                        { value: 'PostalService', label: 'Postal Service' },
                        { value: 'Other', label: 'Other' }
                    ];

                    return (
                        <EditableCell
                            value={record.carrier}
                            record={record}
                            column="carrier"
                            onSave={handleCellSave}
                            placeholder="Select carrier..."
                            loading={isLoading}
                            inputType="autocomplete"
                            autoCompleteOptions={carrierOptions}
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Số theo dõi vận chuyển">Tracking Number</Tooltip>,
                key: 'trackingNumber',
                width: 240,
                render: (text, record) => {
                    const trackingInfo = record.trackingInfo;
                    const hasTracker = trackingInfo && trackingInfo.trackerId;
                    const hasTrackingNumber = record.trackingNumber;
                    const trackerId = trackingInfo?.trackerId;

                    return (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0, width: '100%' }}>
                            {hasTrackingNumber && (
                                <Tooltip title={hasTracker ? `Tracking has been added to Ship24 with ID: ${trackingInfo.trackerId}` : 'Tracking has not been added to Ship24'}>
                                    <div
                                        style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: hasTracker ? '#52c41a' : '#d9d9d9',
                                            flexShrink: 0,
                                            marginTop: 6
                                        }}
                                    />
                                </Tooltip>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <EditableCell
                                    value={record.trackingNumber}
                                    record={record}
                                    column="trackingNumber"
                                    onSave={handleCellSave}
                                    placeholder="Enter tracking..."
                                    loading={isLoading}
                                    inputType="text"
                                    containerStyle={{ width: '100%' }}
                                    displayStyle={{
                                        display: 'block',
                                        lineHeight: 1.35,
                                        whiteSpace: 'normal',
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word'
                                    }}
                                />
                            </div>
                            {trackerId && canUpdateOrders && (
                                <Tooltip title={`Refresh tracking info from Ship24`}>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<SyncOutlined spin={refreshingTrackerIds.has(trackerId)} />}
                                        loading={refreshingTrackerIds.has(trackerId)}
                                        disabled={refreshingTrackerIds.has(trackerId)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRefreshTrackingInfo(trackerId);
                                        }}
                                        style={{
                                            color: '#1890ff',
                                            padding: '0 4px',
                                            height: 20,
                                            minWidth: 20,
                                            flexShrink: 0
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    );
                }
            },
            {
                title: <Tooltip title="Last Mile Tracking">Last Mile</Tooltip>,
                key: 'lastMile',
                width: 240,
                render: (text, record) => {
                    const trackingInfo = record.trackingInfo;

                    if (!trackingInfo || (!trackingInfo.lastMileTracking && !trackingInfo.lastMileCarrier)) {
                        return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
                    }

                    return (
                        <div style={{ lineHeight: 1.3, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                                {trackingInfo.lastMileCarrier && (
                                    <div style={{
                                        fontSize: 10,
                                        color: '#666',
                                        fontWeight: 'bold',
                                        marginBottom: 2,
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {trackingInfo.lastMileCarrier}
                                    </div>
                                )}
                                {trackingInfo.isUpdatedLastMile && (
                                    <Tooltip title="Last mile information has been updated">
                                        <WarningOutlined 
                                            style={{ 
                                                color: '#fa8c16', 
                                                fontSize: 12,
                                                marginBottom: 2
                                            }} 
                                        />
                                    </Tooltip>
                                )}
                            </div>
                            {trackingInfo.lastMileTracking && (
                                <Tooltip title={trackingInfo.lastMileTracking}>
                                    <span style={{
                                        fontSize: 11,
                                        color: '#1890ff',
                                        fontFamily: 'monospace',
                                        display: 'block',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {trackingInfo.lastMileTracking}
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    );
                }
            },
            {
                title: <Tooltip title="Trạng thái vận chuyển">Shipment Status</Tooltip>,
                key: 'shipmentStatus',
                width: 170,
                align: 'center',
                render: (text, record) => {
                    const trackingInfo = record.trackingInfo;
                    const currentStatus = trackingInfo?.lastStatus;
                    const lastScrapedAt = trackingInfo?.lastScrapedAt;

                    const shipmentStatusOptions = [
                        { value: 'info_received', label: 'Info. Received' },
                        { value: 'in_transit', label: 'In Transit' },
                        { value: 'out_for_delivery', label: 'Out for Delivery' },
                        { value: 'failed_attempt', label: 'Failed Attempt' },
                        { value: 'available_for_pickup', label: 'To Pick Up' },
                        { value: 'delivered', label: 'Delivered' },
                        { value: 'exception', label: 'Exception' },
                        { value: 'pending', label: 'Pending' }
                    ];

                    // Status color mapping
                    const getStatusColor = (status) => {
                        switch (status?.toLowerCase()) {
                            case 'delivered':
                                return 'green';
                            case 'out_for_delivery':
                                return 'blue';
                            case 'in_transit':
                                return 'processing';
                            case 'info_received':
                                return 'cyan';
                            case 'available_for_pickup':
                                return 'purple';
                            case 'failed_attempt':
                                return 'orange';
                            case 'exception':
                                return 'red';
                            case 'pending':
                                return 'gold';
                            default:
                                return 'default';
                        }
                    };

                    // Format status text for display
                    const formatStatus = (status) => {
                        if (!status) return 'Unknown';
                        return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    };

                    // Get icon for status
                    const getStatusIcon = (status) => {
                        switch (status?.toLowerCase()) {
                            case 'in_transit':
                                return <TruckIcon style={{ marginRight: 4, fontSize: 14 }} />;
                            case 'delivered':
                                return <DeliveredIcon style={{ marginRight: 4, fontSize: 14 }} />;
                            case 'out_for_delivery':
                                return <CarOutlined style={{ marginRight: 4, fontSize: 14 }} />;
                            default:
                                return null;
                        }
                    };

                    return (
                        <EditableCell
                            value={currentStatus}
                            record={record}
                            column="shipmentStatus"
                            onSave={handleCellSave}
                            options={shipmentStatusOptions}
                            placeholder="Select status..."
                            loading={isLoading}
                            inputType="select"
                            displayComponent={(currentValue) => (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{
                                        fontSize: 10,
                                        color: '#999',
                                        lineHeight: 1.2
                                    }}>
                                        Scraped at: {lastScrapedAt ? dayjs(lastScrapedAt).format('MM/DD HH:mm') : '--/--'}
                                    </div>
                                    {currentValue ? (
                                        <Tag
                                            color={getStatusColor(currentValue)}
                                            style={{
                                                marginTop: 1,
                                                marginRight: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {getStatusIcon(currentValue)}
                                            {formatStatus(currentValue)}
                                        </Tag>
                                    ) : (
                                        <span style={{ color: '#999', fontSize: 11 }}>Click to set</span>
                                    )}
                                </div>
                            )}
                        />
                    );
                }
            },
            {
                title: <Tooltip title="Chi tiết vận chuyển">Shipment Details</Tooltip>,
                key: 'shipmentDetails',
                width: 320,
                align: 'left',
                ellipsis: true,
                render: (text, record) => {
                    const trackingInfo = record.trackingInfo;

                    if (!trackingInfo || !trackingInfo.scrapeData || !trackingInfo.scrapeData.events || trackingInfo.scrapeData.events.length === 0) {
                        return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
                    }

                    // Get the first event (latest event)
                    const firstEvent = trackingInfo.scrapeData.events[0];

                    if (!firstEvent) {
                        return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
                    }

                    return (
                        <div
                            style={{
                                lineHeight: 1.3,
                                cursor: 'pointer',
                                padding: '4px',
                                borderRadius: '4px',
                            }}
                            onClick={() => {
                                setSelectedTrackingInfo(trackingInfo);
                                setIsShippingTimelineModalVisible(true);
                            }}
                        >
                            {firstEvent.location && (
                                <div style={{
                                    color: '#999',
                                    fontSize: 10,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <b>{firstEvent.occurrenceDatetime ? dayjs(firstEvent.occurrenceDatetime).tz('America/Los_Angeles').format('MM/DD HH:mm') : '--:--'}:</b> {firstEvent.location}
                                </div>
                            )}
                            <a style={{
                                color: '#1890ff',
                                marginBottom: 2,
                                textDecoration: 'underline',
                            }}>
                                {firstEvent.status}
                            </a>
                        </div>
                    );
                }
            },
            {
                title: <Tooltip title="Ngày cập nhật">Last Update</Tooltip>,
                dataIndex: ['order', 'lastUpdateDate'],
                key: 'lastUpdateDate',
                width: 160,
                align: 'right',
                render: (text) => <span className="no-line-through">{text ? dayjs(text).format('DD/MM/YYYY HH:mm') : '-'}</span>,
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                }),
            },
            {
                title: (
                    <Tooltip title="Overview trạng thái đơn hàng">
                        <span>
                            Status Overview
                            <StatusOverviewTooltip />
                        </span>
                    </Tooltip>
                ),
                dataIndex: ['order', 'orderStatus'],
                key: 'statusOverview',
                width: 155,
                align: 'center',
                fixed: 'right',
                className: 'status-overview-cell',
                render: (orderStatus, record) => (
                    <span className="no-line-through">
                        <StatusOverview
                            record={record}
                            orderStatus={orderStatus}
                            onMessageClick={handleMessagesClick}
                            allOrderItems={processedData}
                        />
                    </span>
                ),
                onCell: (record) => ({
                    rowSpan: record._isFirstItemInOrder ? record._orderItemCount : 0,
                    className: 'status-overview-cell'
                })
            }
        ];

        // Filter columns based on permissions
        const shippingColumns = ['shippingInfo', 'carrier', 'trackingNumber', 'lastMile', 'shipmentStatus', 'shipmentDetails'];
        return baseColumns.filter(column => {
            // Hide financial columns if user doesn't have permission
            if (isFinancialColumn(column) && !canViewFinancialData) {
                return false;
            }
            // Hide base cost column if user doesn't have permission
            if (isBaseCostColumn(column) && !canViewBaseCost) {
                return false;
            }
            // Hide shipping/tracking/customer columns if user doesn't have permission
            if (shippingColumns.includes(column.key) && !canViewShippingInfo) {
                return false;
            }
            return true;
        });
    }, [pagination.current, pagination.pageSize, printerOptions, categoryOptions, handleCellSave, isLoading, handleMessagesClick, openPaymentDetails, canViewFinancialData, canViewCustomerMessages, canUpdateOrders, canViewSensitiveInfo, canViewShippingInfo, canViewBaseCost]);

    // Calculate unique orders count for display
    const getUniqueOrdersCount = (items) => {
        if (!items || items.length === 0) return 0;
        const uniqueOrderIds = new Set(items.map(item => item.order?.id));
        return uniqueOrderIds.size;
    };

    // Sync dropdown menu items with permission checks
    const syncMenuItems = useMemo(() => {
        const items = [];

        if (canSyncAmazonOrders) {
            items.push({
                key: 'amazon',
                label: 'Sync order items from Amazon',
                icon: <AmazonOutlined />
            });
        }

        if (canSyncAmazonOrders) {
            items.push({
                key: 'financial',
                label: 'Sync Financial Data',
                icon: <DollarOutlined />
            });
        }

        if (canSyncAmazonOrders) {
            items.push({
                key: 'confirm-tracking',
                label: 'Confirm Tracking to Orders',
                icon: <CheckCircleOutlined />
            });
        }

        if (canUploadReport) {
            items.push({
                key: 'upload',
                label: 'Upload Order Report',
                icon: <UploadOutlined />
            });
        }

        return items;
    }, [canSyncAmazonOrders, canUploadReport]);

    // Data dropdown menu items with permission checks
    const dataMenuItems = useMemo(() => {
        const items = [];

        if (canExportCSV) {
            items.push({
                key: 'export',
                label: 'Export to CSV',
                icon: <DownloadOutlined />
            });
        }

        return items;
    }, [canExportCSV]);

    const handleSyncMenuClick = ({ key }) => {
        if (key === 'amazon' && onSyncClick && canSyncAmazonOrders) {
            onSyncClick();
        } else if (key === 'financial' && canSyncAmazonOrders) {
            setIsSyncFinancialModalVisible(true);
        } else if (key === 'confirm-tracking' && canSyncAmazonOrders) {
            setIsConfirmTrackingModalVisible(true);
        } else if (key === 'upload' && canUploadReport) {
            setIsUploadReportModalVisible(true);
        } else {
            message.warning('Bạn không có quyền thực hiện hành động này');
        }
    };

    const handleDataMenuClick = ({ key }) => {
        if (key === 'export' && canExportCSV) {
            // Kiểm tra xem có filter nào được áp dụng không
            const urlParams = new URLSearchParams(window.location.search);
            const hasFilters = Array.from(urlParams.entries()).some(([key, value]) =>
                key !== 'page' && key !== 'pageSize' && value && value.trim() !== ''
            );

            if (!hasFilters) {
                message.warning('Vui lòng áp dụng filter trước khi export CSV');
                return;
            }

            handleExportCSV();
        } else {
            message.warning('Bạn không có quyền thực hiện hành động này');
        }
    };

    // Bulk action menu items with permission checks
    const bulkActionMenuItems = useMemo(() => {
        const items = [];

        if (canBulkEdit) {
            items.push({
                key: 'bulkEdit',
                label: 'Bulk Edit',
                icon: <EditOutlined />,
                disabled: selectedRowKeys.length === 0,
            });
        }

        if (canSendMockup) {
            items.push({
                key: 'sendMockup',
                label: 'Send Mockup',
                icon: <SendOutlined />,
                // Disable if not all selected records are MockupReady
                disabled: selectedRowKeys.length === 0 || !selectedRowKeys.every(key => {
                    const record = processedData.find(item => item.id === key);
                    return record && record.customizationStatus === 'MockupReady';
                }),
            });
        }

        if (canSendFollowUpMessage) {
            items.push({
                key: 'sendFollowUpMessage',
                label: 'Send Follow-up Message',
                icon: <MessageOutlined />,
                // Disable if no selected records or none are overdue
                disabled: selectedRowKeys.length === 0,
            });
        }

        if (canSendBulkMessage) {
            items.push({
                key: 'sendBulkMessage',
                label: 'Send Custom Message',
                icon: <SendOutlined />,
                disabled: selectedRowKeys.length === 0,
            });
        }

        if (canFulfillToPrinter) {
            items.push({
                key: 'fulfillToPrinter',
                label: 'Fulfill to Printer',
                icon: <ProductOutlined />,
                disabled: selectedRowKeys.length === 0,
            });
        }

        if (canConfirmShipment) {
            items.push({
                key: 'confirmShipment',
                label: 'Confirm Shipment',
                icon: <CheckCircleOutlined />,
                disabled: selectedRowKeys.length === 0 || !selectedRowKeys.every(key => {
                    const record = processedData.find(item => item.id === key);
                    return record && record.trackingNumber && record.carrier && !record.isConfirmShipped;
                }),
            });
        }

        if (canManageOrders) {
            if (items.length > 0) {
                items.push({ type: 'divider' });
            }
            const eligibleForReset = selectedRowKeys.some(key => {
                const record = processedData.find(item => item.id === key);
                return record &&
                    record.order?.fulfillmentChannel === 'MFN' &&
                    !record.customizationInfo &&
                    Number(record.quantityOrdered || 0) > 0 &&
                    record.fulfillmentStatus !== 'Canceled';
            });
            items.push({
                key: 'resetOrderTempStatus',
                label: 'Fix Order Processing Temp',
                icon: <ReloadOutlined />,
                disabled: selectedRowKeys.length === 0 || !eligibleForReset,
            });
        }

        return items;
    }, [canBulkEdit, canSendMockup, canSendFollowUpMessage, canSendBulkMessage, canFulfillToPrinter, canConfirmShipment, canManageOrders, selectedRowKeys, processedData]);

    // Memoized bulk action handlers with permission checks
    const handleBulkActionMenuClick = useCallback(({ key }) => {
        console.log('Bulk action clicked:', key, 'Selected rows:', selectedRowKeys);

        if (key === 'bulkEdit') {
            if (!canBulkEdit) {
                message.warning('Bạn không có quyền chỉnh sửa hàng loạt');
                return;
            }
            // Open bulk edit modal
            setIsBulkEditModalVisible(true);
        } else if (key === 'sendMockup') {
            if (!canSendMockup) {
                message.warning('Bạn không có quyền gửi mockup');
                return;
            }
            // Filter selected records that are MockupReady
            const eligibleOrders = processedData
                .filter(item => selectedRowKeys.includes(item.id) && item.customizationStatus === 'MockupReady')
                .map(item => ({
                    id: item.id,
                    orderItemId: item.orderItemId,
                    amazonOrderId: item.order?.amazonOrderId,
                    title: item.title,
                    sellerSKU: item.sellerSKU,
                    purchaseDate: item.order?.purchaseDate,
                    customerName: item.order?.shipName || 'N/A'
                }));

            setMockupReadyOrders(eligibleOrders);
            setIsSendMockupModalVisible(true);
        } else if (key === 'sendFollowUpMessage') {
            if (!canSendFollowUpMessage) {
                message.warning('Bạn không có quyền gửi tin nhắn tiếp tục');
                return;
            }
            // Filter selected records for follow-up messages
            const eligibleOrders = processedData
                .filter(item => selectedRowKeys.includes(item.id))
                .map(item => ({
                    id: item.id,
                    orderItemId: item.orderItemId,
                    amazonOrderId: item.order?.amazonOrderId,
                    title: item.title,
                    sellerSKU: item.sellerSKU,
                    purchaseDate: item.order?.purchaseDate,
                    customerName: item.order?.shipName || 'N/A',
                    customizationStatus: item.customizationStatus,
                    mockupSentDate: item.mockupSentDate,
                    daysSinceMockup: item.mockupSentDate ? dayjs().diff(dayjs(item.mockupSentDate), 'day') : null
                }));

            setOverdueOrders(eligibleOrders);
            setIsSendFollowUpMessageModalVisible(true);
        } else if (key === 'sendBulkMessage') {
            if (!canSendBulkMessage) {
                message.warning('Bạn không có quyền gửi tin nhắn hàng loạt');
                return;
            }
            // Get selected records for bulk message
            const selectedOrders = processedData.filter(item => selectedRowKeys.includes(item.id));
            setSelectedOrdersForBulkMessage(selectedOrders);
            setIsSendBulkMessageModalVisible(true);
        } else if (key === 'fulfillToPrinter') {
            if (!canFulfillToPrinter) {
                message.warning('Bạn không có quyền fulfill to printer');
                return;
            }
            // Filter selected records for fulfill to printer
            const selectedOrders = processedData
                .filter(item => selectedRowKeys.includes(item.id))
                .map(item => ({
                    id: item.id,
                    orderItemNumber: item.orderItemId,
                    orderNumber: item.order?.amazonOrderId,
                    title: item.title,
                    sku: item.sellerSKU,
                    purchaseDate: item.order?.purchaseDate,
                    customerName: item.order?.shipName || 'N/A',
                    printerName: printers?.find(p => p.id === item.printerId)?.name || null,
                    customizationStatus: item.customizationStatus,
                    // Add shipping address information for validation
                    shipAddressLine1: item.order?.shipAddressLine1,
                    shipCity: item.order?.shipCity,
                    shipCountryCode: item.order?.shipCountryCode
                }));

            setSelectedOrdersForFulfill(selectedOrders);
            setIsFulfillToPrinterModalVisible(true);
        } else if (key === 'confirmShipment') {
            if (!canConfirmShipment) {
                message.warning('Bạn không có quyền xác nhận shipment');
                return;
            }
            // Filter selected records that have tracking number and carrier but not yet confirmed shipped
            const eligibleOrders = processedData
                .filter(item => selectedRowKeys.includes(item.id) && item.trackingNumber && item.carrier && !item.isConfirmShipped)
                .map(item => ({
                    id: item.id,
                    orderItemId: item.orderItemId,
                    amazonOrderId: item.order?.amazonOrderId,
                    title: item.title,
                    sellerSKU: item.sellerSKU,
                    purchaseDate: item.order?.purchaseDate,
                    customerName: item.order?.shipName || 'N/A',
                    trackingNumber: item.trackingNumber,
                    carrier: item.carrier,
                    fulfillmentStatus: item.fulfillmentStatus,
                    lastMileCarrier: item.trackingInfo?.lastMileCarrier,
                    lastMileTrackingNumber: item.trackingInfo?.lastMileTracking
                }));

            setShipmentReadyOrders(eligibleOrders);
            setIsConfirmShipmentModalVisible(true);
        } else if (key === 'resetOrderTempStatus') {
            if (!canManageOrders) {
                message.warning('Bạn không có quyền thực hiện hành động này');
                return;
            }
            // Chỉ lấy amazonOrderId của các item MFN + Waiting for sync (chưa có customizationInfo)
            const orderIds = [...new Set(
                processedData
                    .filter(item =>
                        selectedRowKeys.includes(item.id) &&
                        item.order?.fulfillmentChannel === 'MFN' &&
                        !item.customizationInfo &&
                        Number(item.quantityOrdered || 0) > 0 &&
                        item.fulfillmentStatus !== 'Canceled'
                    )
                    .map(item => item.order?.amazonOrderId)
                    .filter(Boolean)
            )];
            if (orderIds.length === 0) {
                message.warning('Không có order item nào hợp lệ (cần Customization: Waiting for sync)');
                return;
            }
            setResetOrderTempIds(orderIds);
            setIsResetOrderTempModalVisible(true);
        }
    }, [selectedRowKeys, processedData, printers, canBulkEdit, canSendMockup, canSendFollowUpMessage, canSendBulkMessage, canFulfillToPrinter, canConfirmShipment, canManageOrders]);

    // Handle bulk edit
    const handleBulkEdit = useCallback(async (updateData) => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one item to edit');
            return;
        }

        try {
            console.log('Bulk editing:', selectedRowKeys.length, 'items with data:', updateData);

            const result = await dispatch(bulkUpdateOrderItems(selectedRowKeys, updateData));

            if (result.success) {
                message.success(result.message);
                // setSelectedRowKeys([]); // Clear selection after successful update
                setIsBulkEditModalVisible(false);

                // Refresh the data to show updated values without triggering pagination change
                // This prevents clearSelectedRowsSignal from being triggered
                dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error('Error bulk editing items:', error);
            message.error('Có lỗi xảy ra khi cập nhật hàng loạt');
        }
    }, [selectedRowKeys, dispatch, pagination, onPaginationChange]);

    // Handle upload report
    const handleUploadReport = useCallback(async (formData) => {
        try {
            console.log('Uploading order report...');

            const result = await dispatch(uploadOrderReport(formData));

            if (result.success) {
                message.success(result.message || 'Order report uploaded successfully');
                setIsUploadReportModalVisible(false);

                // Refresh the data to show new orders
                if (onPaginationChange) {
                    onPaginationChange(pagination.current, pagination.pageSize);
                } else {
                    dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
                }
            } else {
                message.error(result.message || 'Failed to upload order report');
            }
        } catch (error) {
            console.error('Error uploading order report:', error);
            message.error('Có lỗi xảy ra khi upload order report');
        }
    }, [dispatch, pagination, onPaginationChange]);

    // Handle trigger report sync
    const handleTriggerReportSync = useCallback(async (sellerAccountId) => {
        try {
            console.log('Triggering report sync for seller account:', sellerAccountId);

            const result = await dispatch(triggerReportSync(sellerAccountId));

            if (result.success) {
                message.success(result.message || 'Amazon Order Reports sync triggered successfully');
                setIsUploadReportModalVisible(false);
                
                // Show additional info if available
                if (result.data?.workerName) {
                    message.info(`Worker ${result.data.workerName} sẽ thực hiện sync trong vài phút`, 5);
                }
            } else {
                message.error(result.message || 'Failed to trigger sync');
            }
        } catch (error) {
            console.error('Error triggering report sync:', error);
            message.error('Có lỗi xảy ra khi trigger sync Amazon Order Reports');
        }
    }, [dispatch]);
    // Handle trigger order processing
    const handleTriggerOrderProcessing = useCallback(async () => {
        try {
            console.log('Triggering order processing...');

            const result = await dispatch(triggerOrderProcessing());

            if (result.success) {
                message.success(result.message || 'Order processing job triggered successfully');
                setIsUploadReportModalVisible(false);
                
                message.info('Main worker sẽ xử lý các orders trong vài phút', 5);
            } else {
                message.error(result.message || 'Failed to trigger order processing');
            }
        } catch (error) {
            console.error('Error triggering order processing:', error);
            message.error('Có lỗi xảy ra khi trigger order processing job');
        }
    }, [dispatch]);

    // Handle send bulk message
    const handleSendBulkMessage = useCallback(async (result) => {
        try {
            console.log('Bulk message sent successfully:', result);
            message.success(`Bulk message sent to ${selectedOrdersForBulkMessage.length} orders successfully!`);
            
            // Clear selection and close modal
            setSelectedRowKeys([]);
            setSelectedOrdersForBulkMessage([]);
            setIsSendBulkMessageModalVisible(false);

            // Refresh the data
            if (onPaginationChange) {
                onPaginationChange(pagination.current, pagination.pageSize);
            } else {
                dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
            }
        } catch (error) {
            console.error('Error in bulk message callback:', error);
            message.error('Có lỗi xảy ra sau khi gửi tin nhắn hàng loạt');
        }
    }, [selectedOrdersForBulkMessage, selectedRowKeys, dispatch, pagination, onPaginationChange]);

    // Handle confirm tracking to Amazon
    const handleConfirmTracking = useCallback(async (selectedIds) => {
        if (!selectedIds || selectedIds.length === 0) {
            message.warning('Vui lòng chọn ít nhất một seller account');
            return;
        }

        setConfirmTrackingLoading(true);
        try {
            const result = await dispatch(triggerAutoConfirmTracking({
                sellerAccountIds: selectedIds
            }));

            if (result.success) {
                message.success('Đã bắt đầu xác nhận tracking cho các đơn hàng. Quá trình này có thể mất vài phút.');
                setIsConfirmTrackingModalVisible(false);
            } else {
                message.error(result.message || 'Có lỗi xảy ra khi kích hoạt xác nhận tracking');
            }
        } catch (error) {
            console.error('Error confirming tracking:', error);
            message.error('Có lỗi xảy ra khi kích hoạt xác nhận tracking');
        } finally {
            setConfirmTrackingLoading(false);
        }
    }, [dispatch]);

    // Handle sync financial data
    const handleSyncFinancial = useCallback(async (selectedIds, dateRange) => {
        if (!selectedIds || selectedIds.length === 0) {
            message.warning('Vui lòng chọn ít nhất một seller account');
            return;
        }

        setSyncFinancialLoading(true);
        try {
            // Convert dateRange array to postedAfter/postedBefore
            // dateRange[0] = start date (YYYY-MM-DD), dateRange[1] = end date (YYYY-MM-DD)
            const postedAfter = dateRange && dateRange[0] ? `${dateRange[0]}T00:00:00.000Z` : null;
            const postedBefore = dateRange && dateRange[1] ? `${dateRange[1]}T23:59:59.999Z` : null;

            const result = await dispatch(syncFinancialData({
                sellerAccountIds: selectedIds,
                postedAfter,
                postedBefore
            }));

            if (result.success) {
                message.success('Đã bắt đầu đồng bộ dữ liệu tài chính. Quá trình này có thể mất vài phút.');
                setIsSyncFinancialModalVisible(false);
            } else {
                message.error(result.error || 'Có lỗi xảy ra khi đồng bộ dữ liệu tài chính');
            }
        } catch (error) {
            console.error('Error syncing financial data:', error);
            message.error('Có lỗi xảy ra khi đồng bộ dữ liệu tài chính');
        } finally {
            setSyncFinancialLoading(false);
        }
    }, [dispatch]);

    // Handle export CSV
    const handleExportCSV = useCallback(async () => {
        try {
            message.loading({ content: 'Đang export CSV...', key: 'export-csv', duration: 0 });

            // Build export params from Redux filters (same logic as fetchOrderItems)
            const exportParams = {};

            if (filters.searchText) exportParams.search = filters.searchText;
            if (filters.selectedStatus && filters.selectedStatus !== 'all') exportParams.status = filters.selectedStatus;
            if (filters.selectedDateRange && filters.selectedDateRange.length === 2) {
                exportParams.startDate = dayjs(filters.selectedDateRange[0]).format('YYYY-MM-DD');
                exportParams.endDate = dayjs(filters.selectedDateRange[1]).format('YYYY-MM-DD');
            }
            if (filters.selectedSellerAccount && filters.selectedSellerAccount !== 'all') exportParams.sellerAccountId = filters.selectedSellerAccount;
            if (filters.selectedCustomizationStatus && filters.selectedCustomizationStatus !== 'all') exportParams.customizationStatus = filters.selectedCustomizationStatus;
            if (filters.selectedPrinter && filters.selectedPrinter !== 'all') exportParams.printerId = filters.selectedPrinter;
            if (filters.selectedFulfillmentStatus && filters.selectedFulfillmentStatus !== 'all') exportParams.fulfillmentStatus = filters.selectedFulfillmentStatus;
            if (filters.selectedFulfillmentChannel && filters.selectedFulfillmentChannel !== 'all') exportParams.fulfillmentChannel = filters.selectedFulfillmentChannel;
            if (filters.selectedShipmentStatus && filters.selectedShipmentStatus !== 'all') exportParams.shipmentStatus = filters.selectedShipmentStatus;
            if (filters.selectedSupporter && filters.selectedSupporter !== 'all') exportParams.supporterId = filters.selectedSupporter;
            if (filters.selectedCategory && filters.selectedCategory !== 'all') exportParams.categoryId = filters.selectedCategory;
            if (filters.selectedMessageStatus && filters.selectedMessageStatus !== 'all') exportParams.messageStatus = filters.selectedMessageStatus;
            if (filters.selectedShipDateDue && filters.selectedShipDateDue !== 'all') exportParams.shipDateDue = filters.selectedShipDateDue;
            if (filters.selectedDeliveryDateDue && filters.selectedDeliveryDateDue !== 'all') exportParams.deliveryDateDue = filters.selectedDeliveryDateDue;
            if (filters.selectedConfirmShipment && filters.selectedConfirmShipment !== 'all') exportParams.confirmShipment = filters.selectedConfirmShipment;
            if (filters.selectedUpdatedLastMile && filters.selectedUpdatedLastMile !== 'all') exportParams.updatedLastMile = filters.selectedUpdatedLastMile;
            if (filters.selectedMockupSentAt && filters.selectedMockupSentAt !== 'all') exportParams.mockupSentAt = filters.selectedMockupSentAt;
            if (filters.selectedTitle && filters.selectedTitle.trim()) exportParams.title = filters.selectedTitle.trim();
            if (filters.selectedSellerSku && filters.selectedSellerSku.trim()) exportParams.sellerSku = filters.selectedSellerSku.trim();
            if (filters.selectedEmployeeId && filters.selectedEmployeeId.trim()) exportParams.employeeId = filters.selectedEmployeeId.trim();
            if (filters.selectedExcludeTitle && filters.selectedExcludeTitle.trim()) exportParams.excludeTitle = filters.selectedExcludeTitle.trim();
            if (filters.selectedExcludeSellerSku && filters.selectedExcludeSellerSku.trim()) exportParams.excludeSellerSku = filters.selectedExcludeSellerSku.trim();
            if (filters.selectedBasecost && filters.selectedBasecost !== 'all') exportParams.basecost = filters.selectedBasecost;
            if (filters.selectedQuantity !== null && filters.selectedQuantity !== undefined && filters.selectedQuantity !== '') exportParams.quantity = filters.selectedQuantity;

            const response = await OrderService.exportOrderItemsCSV(exportParams);

            // Create blob and download file
            const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            // Get filename from response headers or generate default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'order_items_export.csv';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            message.success({ content: 'Export CSV thành công!', key: 'export-csv' });
        } catch (error) {
            console.error('Error exporting CSV:', error);
            message.error({ content: 'Lỗi khi export CSV', key: 'export-csv' });
        }
    }, [filters]);

    // Handle pagination change for the small pagination component
    const handleSmallPaginationChange = (page, pageSize) => {
        if (onPaginationChange) {
            onPaginationChange(page, pageSize);
        } else {
            // Fallback to old behavior
            dispatch(fetchOrderItems(page, pageSize));
        }
    };

    const handleMessageUpdate = useCallback(async () => {
        // Refresh the orders data to update message counts
        try {
            // This will refresh the current page with current filters
            if (onPaginationChange) {
                onPaginationChange(pagination.current, pagination.pageSize);
            } else {
                dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
            }
        } catch (error) {
            console.error('Error refreshing orders after message update:', error);
        }
    }, [dispatch, onPaginationChange, pagination.current, pagination.pageSize]);

    // Optimized getRowClassName with useCallback
    const getRowClassName = useCallback((record, index) => {
        let className = index % 2 === 0 ? 'table-row-even' : 'table-row-odd';

        // Nếu đơn hàng có trạng thái Canceled, áp dụng class canceled
        if (record.order?.orderStatus === 'Canceled' || record.fulfillmentStatus === 'Canceled') {
            className = 'table-row-canceled';
        }

        // Kiểm tra nếu là item cuối cùng của order hiện tại dựa trên orderId
        const items = processedData;
        const nextRow = items[index + 1];
        if (!nextRow || nextRow.order?.id !== record.order?.id) {
            className += ' order-group-last-item';
        }

        return className;
    }, [processedData]);

    return (
        <>
            <Row justify="space-between" align="middle" className='orders-list-header'>
                <Col>
                    <Space direction='horizontal' size={4}>
                        <Text strong style={{ fontSize: 14 }}>
                            Total Order Items:
                        </Text>
                        {isLoading && pagination?.total === 0 ? (
                            <Text style={{ color: '#999' }}>Loading...</Text>
                        ) : (
                            <Pagination
                                size="small"
                                current={pagination?.current || 1}
                                total={pagination?.total || 0}
                                pageSize={pagination?.pageSize || 10}
                                onChange={handleSmallPaginationChange}
                                showSizeChanger={false}
                                showQuickJumper={false}
                                showTotal={(total, range) => {
                                    // Handle edge case where total is 0
                                    if (total === 0) {
                                        return '0 của 0';
                                    }
                                    return `${range[0]}-${range[1]} của ${total}`;
                                }}
                            />
                        )}
                    </Space>
                </Col>
                <Col>
                    <Space>
                        {/* Show Bulk Actions dropdown only if user has any bulk action permissions */}
                        {(canBulkEdit || canSendMockup || canSendFollowUpMessage || canSendBulkMessage || canFulfillToPrinter || canConfirmShipment) && (
                            <Badge count={selectedRowKeys.length} offset={[-125, -5]} showZero={false}>
                                <Dropdown
                                    menu={{
                                        items: bulkActionMenuItems,
                                        onClick: handleBulkActionMenuClick
                                    }}
                                    trigger={['hover']}
                                    disabled={selectedRowKeys.length === 0}
                                >
                                    <a style={{
                                        color: selectedRowKeys.length === 0 ? '#d9d9d9' : undefined,
                                        cursor: selectedRowKeys.length === 0 ? 'not-allowed' : 'pointer'
                                    }}>
                                        <ThunderboltOutlined /> Bulk Actions <CaretDownFilled />
                                    </a>
                                </Dropdown>
                            </Badge>
                        )}
                        {/* Show divider only if both bulk actions and other menus are available */}
                        {(canBulkEdit || canSendMockup || canSendFollowUpMessage || canSendBulkMessage || canFulfillToPrinter || canConfirmShipment) &&
                            (canSyncAmazonOrders || canUploadReport || canExportCSV) && (
                                <Divider type="vertical" />
                            )}
                        {/* Show Data dropdown only if user has data permissions */}
                        {canExportCSV && (
                            <Dropdown
                                menu={{
                                    items: dataMenuItems,
                                    onClick: handleDataMenuClick
                                }}
                                trigger={['hover']}
                            >
                                <a>
                                    <DatabaseOutlined /> Data <CaretDownFilled />
                                </a>
                            </Dropdown>
                        )}
                        {/* Show divider between Data and Sync menus */}
                        {canExportCSV && (canSyncAmazonOrders || canUploadReport) && (
                            <Divider type="vertical" />
                        )}
                        {/* Show Sync dropdown only if user has sync permissions */}
                        {(canSyncAmazonOrders || canUploadReport) && (
                            <Dropdown
                                menu={{
                                    items: syncMenuItems,
                                    onClick: handleSyncMenuClick
                                }}
                                trigger={['hover']}
                            >
                                <a>
                                    <SyncOutlined /> Sync <CaretDownFilled />
                                </a>
                            </Dropdown>
                        )}
                    </Space>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={processedData}
                loading={isLoading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey="id"
                rowClassName={getRowClassName}
                scroll={{ x: 3820 }}
                sticky={{ offsetHeader: 85, offsetScroll: 32 }}
                rowSelection={rowSelection}
                // Add these performance optimizations
                virtual={false} // Disable virtualization if causing issues
                showSorterTooltip={false} // Reduce tooltip overhead
                size="middle" // Consistent sizing
                bordered={false} // Reduce border rendering overhead
            />

            <ShippingInfoModal
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                selectedOrder={selectedOrder}
                onOrderUpdated={handleOrderUpdated}
            />

            <CustomizationInfoModal
                visible={isCustomizationModalVisible}
                onCancel={() => setIsCustomizationModalVisible(false)}
                selectedCustomization={selectedCustomization}
            />

            <SendMockupModal
                visible={isSendMockupModalVisible && canSendMockup}
                onCancel={() => setIsSendMockupModalVisible(false)}
                onConfirm={async () => {
                    if (!canSendMockup) {
                        message.warning('Bạn không có quyền gửi mockup');
                        return;
                    }
                    try {
                        // Extract order item IDs from mockupReadyOrders
                        const orderItemIds = mockupReadyOrders.map(order => order.id);

                        console.log('Sending mockup for order item IDs:', orderItemIds);

                        // Call sendMockup action
                        const result = await dispatch(sendMockup(orderItemIds, pagination));

                        if (result.success) {
                            message.success(result.message);
                            // setSelectedRowKeys([]); // Clear selection after successful send
                        } else {
                            message.error(result.message);
                        }
                    } catch (error) {
                        console.error('Error sending mockup:', error);
                        message.error('Có lỗi xảy ra khi gửi mockup');
                    } finally {
                        setIsSendMockupModalVisible(false);
                    }
                }}
                mockupReadyOrders={mockupReadyOrders}
                loading={isLoading}
            />

            <SendFollowUpMessageModal
                visible={isSendFollowUpMessageModalVisible && canSendFollowUpMessage}
                onCancel={() => setIsSendFollowUpMessageModalVisible(false)}
                onConfirm={async () => {
                    if (!canSendFollowUpMessage) {
                        message.warning('Bạn không có quyền gửi tin nhắn tiếp tục');
                        return;
                    }
                    try {
                        // Extract order item IDs from overdueOrders
                        const orderItemIds = overdueOrders.map(order => order.id);

                        console.log('Sending follow-up message for order item IDs:', orderItemIds);

                        // Call sendFollowUpMessage action
                        const result = await dispatch(sendFollowUpMessage(orderItemIds, pagination));

                        if (result.success) {
                            message.success(result.message);
                            // setSelectedRowKeys([]); // Clear selection after successful send
                        } else {
                            message.error(result.message);
                        }
                    } catch (error) {
                        console.error('Error sending follow-up message:', error);
                        message.error('Có lỗi xảy ra khi gửi tin nhắn tiếp tục');
                    } finally {
                        setIsSendFollowUpMessageModalVisible(false);
                    }
                }}
                overdueOrders={overdueOrders}
                loading={isLoading}
            />

            <FulfillToPrinterModal
                visible={isFulfillToPrinterModalVisible && canFulfillToPrinter}
                platform="amazon"
                onCancel={() => {
                    setIsFulfillToPrinterModalVisible(false);
                    setSelectedOrdersForFulfill([]);
                }}
                onConfirm={async (responseData) => {
                    if (!canFulfillToPrinter) {
                        message.warning('Bạn không có quyền fulfill to printer');
                        return;
                    }
                    try {
                        // Show success message
                        message.success({
                            content: `Fulfill to printer request submitted successfully! ${responseData.totalOrderItems || 0} items are being processed.`,
                            duration: 5
                        });

                        // Close modal and clear selections
                        setIsFulfillToPrinterModalVisible(false);
                        setSelectedOrdersForFulfill([]);
                        // setSelectedRowKeys([]);

                        // Optionally refresh the orders list to see updated status
                        // You may want to add a small delay before refreshing
                        setTimeout(() => {
                            // Trigger a refresh of the orders list if needed
                            window.location.reload(); // Simple refresh, or implement a more sophisticated refresh
                        }, 2000);

                    } catch (error) {
                        console.error('Error in fulfill to printer:', error);
                        message.error({
                            content: `Failed to fulfill to printer: ${error.message || 'Unknown error'}`,
                            duration: 8
                        });
                    }
                }}
                selectedOrders={selectedOrdersForFulfill}
                loading={isLoading}
            />

            <ConfirmShipmentModal
                visible={isConfirmShipmentModalVisible && canConfirmShipment}
                onCancel={() => {
                    setIsConfirmShipmentModalVisible(false);
                    setShipmentReadyOrders([]);
                }}
                onConfirm={async (validOrders) => {
                    if (!canConfirmShipment) {
                        message.warning('Bạn không có quyền xác nhận shipment');
                        return;
                    }
                    try {
                        // Extract order item IDs from valid orders only
                        const orderItemIds = validOrders.map(order => order.id);
                        console.log('Confirming shipment for order item IDs:', orderItemIds);

                        // Call confirmShipment action
                        const result = await dispatch(confirmShipment(orderItemIds, pagination));

                        if (result.success) {
                            message.success(result.message);
                            // setSelectedRowKeys([]); // Clear selection after successful confirmation
                        } else {
                            message.error(result.message);
                        }
                    } catch (error) {
                        console.error('Error confirming shipment:', error);
                        message.error('Có lỗi xảy ra khi xác nhận shipment');
                    } finally {
                        setIsConfirmShipmentModalVisible(false);
                    }
                }}
                shipmentReadyOrders={shipmentReadyOrders}
                loading={isLoading}
            />

            <CustomerMessagesModal
                visible={isCustomerMessagesModalVisible && canViewCustomerMessages}
                onCancel={() => {
                    setIsCustomerMessagesModalVisible(false);
                    setSelectedOrderMessages([]);
                    setSelectedOrderInfo(null);
                }}
                orderMessages={selectedOrderMessages}
                onMessageUpdate={handleMessageUpdate}
                orderId={selectedOrderInfo?.id}
                amazonOrderId={selectedOrderInfo?.amazonOrderId}
            />

            <UploadOrderReportModal
                visible={isUploadReportModalVisible && canUploadReport}
                onCancel={() => setIsUploadReportModalVisible(false)}
                onUpload={handleUploadReport}
                onTriggerSync={handleTriggerReportSync}
                onTriggerOrderProcessing={handleTriggerOrderProcessing}
            />

            <BulkEditModal
                visible={isBulkEditModalVisible && canBulkEdit}
                onCancel={() => setIsBulkEditModalVisible(false)}
                onConfirm={handleBulkEdit}
                selectedOrderItems={processedData.filter(item => selectedRowKeys.includes(item.id))}
                userOptions={userOptions}
                printerOptions={printerOptions}
                categoryOptions={categoryOptions}
                loading={isLoading}
            />

            <ShippingTimelineModal
                visible={isShippingTimelineModalVisible}
                onCancel={() => {
                    setIsShippingTimelineModalVisible(false);
                    setSelectedTrackingInfo(null);
                }}
                trackingInfo={selectedTrackingInfo}
            />

            <SendBulkMessageModal
                visible={isSendBulkMessageModalVisible && canSendBulkMessage}
                onCancel={() => {
                    setIsSendBulkMessageModalVisible(false);
                    setSelectedOrdersForBulkMessage([]);
                }}
                onConfirm={handleSendBulkMessage}
                selectedOrderItems={selectedOrdersForBulkMessage}
                loading={isLoading}
            />

            <SyncFinancialModal
                visible={isSyncFinancialModalVisible && canSyncAmazonOrders}
                onCancel={() => setIsSyncFinancialModalVisible(false)}
                onOk={handleSyncFinancial}
                loading={syncFinancialLoading}
            />

            <ConfirmTrackingModal
                visible={isConfirmTrackingModalVisible && canSyncAmazonOrders}
                onCancel={() => setIsConfirmTrackingModalVisible(false)}
                onOk={handleConfirmTracking}
                loading={confirmTrackingLoading}
            />

            <RefundDetailsModal
                visible={isRefundDetailsModalVisible}
                onCancel={() => {
                    setIsRefundDetailsModalVisible(false);
                    setSelectedRefundDetails(null);
                    setSelectedOrderItem(null);
                }}
                refundDetails={selectedRefundDetails}
                orderItem={selectedOrderItem}
            />

            <PaymentDetailsModal
                visible={isPaymentDetailsModalVisible}
                onCancel={() => {
                    setIsPaymentDetailsModalVisible(false);
                    setSelectedPaymentDetails(null);
                    setSelectedReimbursements([]);
                    setSelectedOrderItem(null);
                }}
                paymentDetails={selectedPaymentDetails}
                orderItem={selectedOrderItem}
                reimbursements={selectedReimbursements}
                loading={paymentDetailsLoading}
            />

            <ResetOrderTempModal
                visible={isResetOrderTempModalVisible}
                onCancel={() => setIsResetOrderTempModalVisible(false)}
                onSuccess={(data) => {
                    message.success(data.message || 'Reset thành công');
                    setIsResetOrderTempModalVisible(false);
                }}
                selectedOrderIds={resetOrderTempIds}
            />
        </>
    );
};

export default OrdersList;
